import { MARKET_CONTENT, MARKET_LOCALES } from "./i18n/market-content";
import { formatMessage, msg, type MessageParams } from './i18n/translate';

/** message is the Chinese text; the web app shows t(error.source, error.params). */
export class RosterDecodeError extends Error {
  constructor(readonly source: string, readonly params?: MessageParams) { super(formatMessage(source, params)); }
}

export type RosterTextEncoding = 'auto' | 'utf-8' | 'big5' | 'gb18030' | 'windows-1252' | 'windows-1254' | 'windows-1258' | 'utf-16le';

// i18n-ignore: matches Chinese column headers
const likelyRosterHeaders = /座號|學號|顯示名稱|學生姓名|姓名|小組|student\s*name|display\s*name|seat\s*no|student\s*id|pr[ée]nom|[ée]l[èe]ve|num[ée]ro|groupe/i;

export function decodeRosterText(buffer: ArrayBuffer, encoding: RosterTextEncoding = 'auto', locale?: string) {
  const bytes = new Uint8Array(buffer);
  const decode = (label: string) => { const value = new TextDecoder(label, { fatal: true, ignoreBOM: false }).decode(bytes); return label === 'windows-1258' ? value.normalize('NFC') : value; };
  if (encoding !== 'auto') {
    try { return decode(encoding); }
    catch { throw new RosterDecodeError(msg('無法以 {encoding} 讀取檔案，請改選正確的 CSV 編碼。'), { encoding }); }
  }
  if (bytes[0] === 0xff && bytes[1] === 0xfe) return decode('utf-16le');
  if (bytes[0] === 0xfe && bytes[1] === 0xff) return decode('utf-16be');
  try { return decode('utf-8'); }
  catch { /* Legacy CSV without a Unicode marker needs a header-based guess. */ }
  // windows-1252 covers old French/English Excel CSVs (Prénom, Élève); it never fails to decode, so the header score decides.
  const preferred = locale === 'tr' ? 'windows-1254' : locale === 'vi' ? 'windows-1258' : null;
  const nativeHeaders = MARKET_LOCALES.flatMap(market => [MARKET_CONTENT[market].roster.seat, MARKET_CONTENT[market].roster.name, MARKET_CONTENT[market].roster.group]).map(text => text.toLowerCase());
  const candidates = [...['big5', 'gb18030', 'windows-1252'], ...(preferred ? [preferred] : [])].flatMap((label) => {
    try {
      const value = decode(label);
      const header = value.slice(0,500).toLowerCase();
      return [{ value, label, score: (value.slice(0, 500).match(new RegExp(likelyRosterHeaders.source, 'gi')) ?? []).length + nativeHeaders.filter(name => header.includes(name)).length }];
    } catch { return []; }
  });
  const best = Math.max(0, ...candidates.map(item => item.score));
  const winners = candidates.filter(item => item.score === best);
  if (best > 0) {
    const unique = [...new Set(winners.map(item => item.value))];
    if (unique.length === 1) return unique[0];
    const local = preferred ? winners.find(item => item.label === preferred) : null;
    if (local) return local.value;
  }
  throw new RosterDecodeError(msg('無法可靠判斷舊版 CSV 編碼，請選擇 Big5、GB18030 或 Windows-1252 後重新讀取。'));
}
